// playlevel.ts ↔ the FRESH-mission-start slice of game/game.cpp PlayLevel/
// InitSimulation (:1577-2081): seed the sync RNG with the SP constant, build
// the SP roster from the level's SideInfos, load the level INTO a new Sim, and
// stand up the live GameSession over it — 'pick mission → play' lands here.
//
// The construction order is the C++'s exactly:
//   1. SetRandomSeed(0x29a)            (game.cpp:1633 — SP is always 0x29a;
//                                       MP seeds gtimm.GetTickCount())
//   2. gplrm.Init(szLvlFilename)       (game.cpp:1664 → Player.cpp:196-238 —
//                                       per-side players from the SideInfos,
//                                       first human = gpplrLocal @ gwfHandicap,
//                                       computers @ kfHcapHard)
//   3. gsim.LoadLevel(szLvlFilename)   (game.cpp:2010 — Level::Init over the
//                                       pack's .lvl + .trmap; Sim.create)
//   4. the gpplrLocal view centering   (Simulation.cpp:253-259 — InitialView
//                                       minus half the sim update map's tile
//                                       size; pinned by the GameSession ctor's
//                                       SetViewPos(fInit) exactly like the
//                                       C++ SetViewPos bounds clamp)
//   5. Game::StartSim                  (the GameSession constructor: forms,
//                                       OnTimer(0) first-update hack, the
//                                       killed post-load trigger pump)

import { Sim } from '../../../sim/src/index.ts';
import type { IniSection, AnirJson } from '../../../sim/src/index.ts';
import type { Level } from '../../../sim/src/level.ts';
import { grvlp, grvlpLarge } from '../../../sim/src/sim/fogmap.ts';
import { WcFromTc } from '../../../sim/src/math/coords.ts';
import type { SimContext } from '../../../sim/src/context.ts';
import { GameSession, type GameSessionOpts } from './gamesession.ts';
import { LevelDataFromPack } from '../io/levelpack.ts';
import type { MissionIdentifier } from '../io/savegame.ts';
import { newEvent, gameOverEvent, knGoSuccess } from './event.ts';
import { CallbackTimer } from './timer.ts';

// game/game.cpp:1633 — "Randomness is good but not REAL randomness."
export const knSPSeed = 0x29a;

export interface PlayLevelOpts {
  /** The mission to start (Shell::PlaySinglePlayer's selection). */
  miid: MissionIdentifier;
  /** gwfHandicap for the local player (game/Player.cpp:230). */
  wfHandicap?: number;
  /** GobTemplates.ini sections + the .anir metadata (as Sim.create). */
  templates: IniSection[];
  anir: AnirJson;
  /** Extra sim host seams (the fog RevealFog seam below is always wired). */
  simHost?: Partial<SimContext['host']>;
  frames?: boolean;
  /** Game::m_dictPvars at level entry — game.cpp:1686-1717 carries pvars
   *  forward across campaign next-level transitions (and a saved game's
   *  envelope load seeds them); empty/absent for a cold start. Seeded
   *  tail-wise (Dictionary::Init order) BEFORE the first update runs. */
  pvars?: readonly { name: string; value: string }[];
}

/** The fog-reveal host seam every live client wires (MobileUnit.cpp ~2970
 *  RevealFog → FogMap::RevealPattern with the small/large pattern). */
export function clientFogHost(): Partial<SimContext['host']> {
  return {
    RevealFog(gob: unknown, fLarge: boolean, tx: number, ty: number) {
      const g = gob as {
        ctx: { fogm: { RevealPattern(tx: number, ty: number, p: unknown): void } };
      };
      g.ctx.fogm.RevealPattern(tx, ty, fLarge ? grvlpLarge : grvlp);
    },
  };
}

/** game.cpp PlayLevel's fresh-start arm + Game::StartSim — returns the running
 *  session (already past the OnTimer(0) first update, exactly like the C++
 *  enters its game loop). sessionOpts carries the shared art stack
 *  (pakr/tam/coords/gameIni/screen) — the GameSession ctor reads
 *  templates/tmapName from here. */
export function PlayLevelSession(
  opts: PlayLevelOpts,
  sessionOpts: Omit<GameSessionOpts, 'sim' | 'templates' | 'tmapName'>,
): GameSession {
  const { pakr, tam, coords } = sessionOpts;

  // the pack-level adapter (the C++ reads the same .lvl/.trmap via gpakr)
  const levelData = LevelDataFromPack(pakr, opts.miid.szLvlFilename);

  // 1-3. seed -> SP roster from SideInfos -> level load (Sim.create owns the
  // ordering; multiplayer:false = the SP sim semantics)
  const sim = Sim.create({
    levelName: opts.miid.szLvlFilename,
    levelData,
    templates: opts.templates,
    anir: opts.anir,
    seed: knSPSeed,
    spHandicap: opts.wfHandicap,
    multiplayer: false,
    host: { ...clientFogHost(), ...opts.simHost },
    frames: opts.frames,
  });

  // Game::m_dictPvars at level entry (game.cpp:1686-1717 carry-forward) —
  // before the GameSession ctor's OnTimer(0) first update, where the
  // MissionLoaded triggers may read them
  if (opts.pvars !== undefined) {
    for (const e of opts.pvars) sim.ctx.pvars.Append(e.name, e.value);
  }

  // 4. the gpplrLocal initial-view centering (Simulation.cpp:253-259): the
  // C++ subtracts half the SIM update map's tile size (updatemap.cpp:29-30 —
  // the sim strip's ceil(px/tile)+1 grid) from the side's InitialView. The
  // raw (unclamped) coords land in m_wx/m_wyView; the GameSession ctor's
  // SetViewPos(..., true) applies the SetViewPos bounds pin (Simulation.cpp:
  // 968-1001) through the input slice exactly as the C++ fInit path does.
  const plrLocal = sim.ctx.plrLocal!;
  const psidi = (sim.sim.GetLevel() as Level).GetSideInfo(plrLocal.GetSide());
  const ptbmMenuButton = tam.CreateTBitmap('menuup.png');
  if (ptbmMenuButton === null) throw new Error('PlayLevelSession: menuup.png missing');
  const cySim = sessionOpts.screen.cy - (ptbmMenuButton.GetSize().cy + 4 * 2);
  const ctxScreen = Math.floor((sessionOpts.screen.cx + coords.gcxTile - 1) / coords.gcxTile) + 1;
  const ctyScreen = Math.floor((cySim + coords.gcyTile - 1) / coords.gcyTile) + 1;
  sim.setViewPos(
    psidi.wptInitialView.wx - Math.trunc(WcFromTc(ctxScreen) / 2),
    psidi.wptInitialView.wy - Math.trunc(WcFromTc(ctyScreen) / 2),
  );

  // the level's tile map name ([General] TileMap, game/Level.cpp:207-214)
  const tmapName = readTileMapName(levelData);

  // 5. Game::StartSim — the GameSession ctor (forms + OnTimer(0) + the killed
  // post-load trigger pump)
  const gs = new GameSession({
    ...sessionOpts,
    sim,
    templates: opts.templates,
    tmapName,
  });
  WireJumpToMission(gs);
  return gs;
}

/** JumpToMissionAction's CLIENT slice (TriggerActions.cpp:1229-1261): the C++
 *  Perform posts gameOverEvent(knGoSuccess) — the game loop latches it and the
 *  PlayLevel do-while advances to m_szNextLevel (SetNextLevel + the TriggerMgr
 *  disable already ran through the sim's own seams). The action chain that
 *  uses it is CutScene -> JumpToMission (s_00's intro): in the C++ the
 *  CutSceneAction BLOCKS in its modal, so the post lands only after the
 *  cutscene is dismissed; the dissolved-modal pump can't block, so the post
 *  waits for the modal stack to drain — preserving the C++ ordering (a post
 *  while the modal is up would force-end it through the form.cpp:595-603
 *  gameOver arm and the player would never read the scene).
 *
 *  Wired here (not the GameSession ctor) for every fresh PlayLevelSession;
 *  embedders that build a GameSession directly (the saved-game load path)
 *  call this on their session too. */
export function WireJumpToMission(gs: GameSession): void {
  gs.sim.ctx.host.JumpToMission = (_pszLevel: string) => {
    const post = (): void => {
      const evt = newEvent(gameOverEvent);
      evt.dw = knGoSuccess;
      gs.evm.PostEvent(evt);
    };
    // The hand-off must wait until the story modal that raised it (s_00's intro
    // CutScene) is FULLY dismissed — otherwise s_01 starts + enqueues its briefing
    // E-Com while the intro is still up and the two OVERLAP. GetModalForm() covers
    // any canvas modal form; the cutscene is a DOM overlay that
    // never lands on the canvas form stack (cutscene.ts returns before LoadForm), so
    // the guard must ALSO consult the DOM overlay state (gs.domStoryOverlayActive —
    // false in a headless harness).
    const modalUp = (): boolean => gs.mfrmm.GetModalForm() !== null || gs.domStoryOverlayActive();
    if (!modalUp()) {
      post();
      return;
    }
    const tmr = new CallbackTimer(() => {
      if (modalUp()) return;
      gs.gtimm.RemoveTimer(tmr);
      post();
    });
    gs.gtimm.AddTimer(tmr, 1);
  };
}

// game/Level.cpp:207-214 — [General] TileMap
function readTileMapName(levelData: { sections: IniSection[] }): string {
  for (const s of levelData.sections) {
    if (s.name !== 'General') continue;
    for (const p of s.props) {
      if (p.key === 'TileMap') return p.value;
    }
  }
  throw new Error('PlayLevelSession: level has no [General] TileMap');
}
